import { mountImageBatch } from './image-batch-queue.js';
import {
  canvasToBlob,
  imageExtension,
  imageIsAr,
  imageName,
  makeCanvas,
  openBitmap,
  createIcoBlob,
  canvasToBMPBlob,
  canvasToTGABlob,
  canvasToTIFFBlob,
  canvasToPDFBlob
} from './image-batch-utils.js';
import { trackEvent } from '../core/analytics.js';

const t = (ar, en) => imageIsAr ? ar : en;

const root = document.getElementById('image-converter-app');
const preset = root?.dataset?.presetFormat || 'image/jpeg';

const defaultFormat = (preset === 'png' || preset === 'image/png')
  ? 'image/png'
  : (preset === 'jpg' || preset === 'jpeg' || preset === 'image/jpeg')
    ? 'image/jpeg'
    : (preset === 'ico' || preset === 'image/x-icon')
      ? 'image/x-icon'
      : preset;

const fields = [
  {
    key: 'format',
    label: t('صيغة الإخراج المطلوبة', 'Target Output Format'),
    type: 'select',
    isFormatHero: true,
    badge: t('تحويل محلي فائق السرعة بدون رفع لخوادم خارجية', 'Ultra-fast local processing with zero server uploads'),
    default: defaultFormat,
    options: [
      {
        value: 'image/jpeg',
        label: t('JPG / JPEG — متوافق عالمياً مع جميع الأجهزة والمواقع بحجم مضغوط', 'JPG / JPEG — Universal compatibility, small file size')
      },
      {
        value: 'image/png',
        label: t('PNG — يحفظ الشفافية بالكامل Alpha وبأعلى دقة بدون أي فقدان', 'PNG — Full transparency (Alpha) & lossless quality')
      },
      {
        value: 'image/webp',
        label: t('WebP — صيغة الويب الحديثة من Google لضغط فائق وسرعة تصفح', 'WebP — Modern web format for extreme compression & speed')
      },
      {
        value: 'image/avif',
        label: t('AVIF — أحدث صيغ الجيل القادم بحجم أصغر بنسبة 50%', 'AVIF — Next-gen codec with up to 50% smaller sizes')
      },
      {
        value: 'image/x-icon',
        label: t('ICO — أيقونات Favicon لمواقع الويب وتطبيقات ويندوز بأحجام متعددة', 'ICO — Multi-size Favicons & Windows application icons')
      },
      {
        value: 'image/bmp',
        label: t('BMP — صيغة ويندوز النقطية القياسية غير المضغوطة Bitmap', 'BMP — Standard uncompressed Windows Bitmap')
      },
      {
        value: 'application/pdf',
        label: t('PDF — تحويل الصورة إلى مستند PDF رقمي عالي الدقة', 'PDF — Convert image into high-resolution PDF document')
      },
      {
        value: 'image/tiff',
        label: t('TIFF — صيغة النشر والطباعة الاحترافية بدون ضغط فاقد', 'TIFF — Professional desktop publishing & uncompressed print')
      },
      {
        value: 'image/x-tga',
        label: t('TGA — صيغة Truevision Targa للألعاب والرسوم ثلاثية الأبعاد 3D', 'TGA — Truevision Targa for game assets & 3D textures')
      },
      {
        value: 'image/gif',
        label: t('GIF — صيغة الصور الرسومية المتوافقة مع كافة المتصفحات والأنظمة', 'GIF — Graphic interchange format compatible everywhere')
      }
    ]
  },
  {
    key: 'quality',
    label: t('جودة الصورة %', 'Image Quality %'),
    type: 'range',
    min: 10,
    max: 100,
    step: 1,
    default: 100,
    suffix: '%',
    showIf: (s) => s.format === 'image/jpeg' || s.format === 'image/webp' || s.format === 'image/avif'
  },
  {
    key: 'icoSize',
    label: t('مقاس أيقونة ICO', 'ICO Resolution'),
    type: 'select',
    default: 'multi',
    options: [
      {
        value: 'multi',
        label: t('حزمة متصفحات الويب الشاملة (16 + 32 + 48 بكسل)', 'Favicon Web Bundle (16+32+48 px)')
      },
      {
        value: 'all',
        label: t('حزمة كاملة لجميع الأنظمة وشاشات العرض (16 + 32 + 48 + 64 + 128 + 256 بكسل)', 'Complete All-in-One Pack (16, 32, 48, 64, 128, 256 px)')
      },
      { value: '256', label: '256×256 px (أيقونات عالية الدقة لنظام Windows)' },
      { value: '128', label: '128×128 px (أيقونة كبيرة للتطبيقات البرمجية)' },
      { value: '64', label: '64×64 px (شاشات Retina عالية الكثافة)' },
      { value: '48', label: '48×48 px (أيقونة سطح مكتب ويندوز الكلاسيكية)' },
      { value: '32', label: '32×32 px (أيقونة شريط المهام وتبويبات المتصفح)' },
      { value: '16', label: '16×16 px (أيقونة Favicon الكلاسيكية لتبويب المتصفح)' }
    ],
    showIf: (s) => s.format === 'image/x-icon'
  },
  {
    key: 'canvasFit',
    label: t('تعديل الكانفاس للأيقونة (إذا لم تكن الصورة 1:1)', 'Canvas Aspect Fit (For Non-Square Images)'),
    type: 'select',
    default: 'pad',
    options: [
      {
        value: 'pad',
        label: t('توسيط ذكي في مربع شفاف (يحفظ كامل الصورة بدون قص أو تشويه)', 'Smart Transparent Pad (Preserves full image without cropping or distortion)')
      },
      {
        value: 'none',
        label: t('بدون تعديل الكانفاس (الاحتفاظ بالنسب الأصلية للصورة)', 'No Canvas Adjustment (Keep original proportions)')
      },
      {
        value: 'crop',
        label: t('قص مربع من مركز الصورة (Center Square Crop)', 'Center Square Crop')
      },
      {
        value: 'stretch',
        label: t('تمديد لملء المربع (Stretch to 1:1)', 'Stretch to fill square (1:1)')
      }
    ],
    showIf: (s) => s.format === 'image/x-icon'
  }
];

if (root) {
  mountImageBatch({
    rootId: 'image-converter-app',
    fields,
    zipName: 'converted-images.zip',
    process: async (file, settings, report) => {
      report(15, t('جارٍ فك تشفير بيانات الصورة محلياً...', 'Decoding image locally...'));
      const bitmap = await openBitmap(file);
      const width = bitmap.width;
      const height = bitmap.height;
      const type = settings.format || defaultFormat;

      const getConvertedFilename = (targetExt) => {
        const cleanBase = imageName(file.name);
        const lowerTarget = targetExt.toLowerCase();
        return `${cleanBase}.${lowerTarget}`;
      };

      if (type === 'image/x-icon') {
        report(40, t('جارٍ معالجة وتجهيز إطارات أيقونة ICO...', 'Generating ICO icon frames...'));
        const fit = settings.canvasFit || 'pad';
        const renderFrame = (targetSize) => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';

          if (fit === 'none') {
            const scale = Math.min(targetSize / width, targetSize / height);
            canvas.width = Math.max(1, Math.round(width * scale));
            canvas.height = Math.max(1, Math.round(height * scale));
            ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
          } else if (fit === 'crop') {
            canvas.width = targetSize;
            canvas.height = targetSize;
            const minD = Math.min(width, height);
            const sx = (width - minD) / 2;
            const sy = (height - minD) / 2;
            ctx.drawImage(bitmap, sx, sy, minD, minD, 0, 0, targetSize, targetSize);
          } else if (fit === 'stretch') {
            canvas.width = targetSize;
            canvas.height = targetSize;
            ctx.drawImage(bitmap, 0, 0, targetSize, targetSize);
          } else {
            // 'pad': smart center pad in transparent square (default)
            canvas.width = targetSize;
            canvas.height = targetSize;
            ctx.clearRect(0, 0, targetSize, targetSize);
            const scale = Math.min(targetSize / width, targetSize / height);
            const dw = Math.round(width * scale);
            const dh = Math.round(height * scale);
            const dx = Math.round((targetSize - dw) / 2);
            const dy = Math.round((targetSize - dh) / 2);
            ctx.drawImage(bitmap, 0, 0, width, height, dx, dy, dw, dh);
          }
          return canvas;
        };

        const frames = (settings.icoSize === 'multi')
          ? [renderFrame(16), renderFrame(32), renderFrame(48)]
          : (settings.icoSize === 'all')
            ? [renderFrame(16), renderFrame(32), renderFrame(48), renderFrame(64), renderFrame(128), renderFrame(256)]
            : [renderFrame(parseInt(settings.icoSize || '32', 10))];

        report(80, t('جارٍ تجميع وتشفير ملف ICO الثنائي...', 'Encoding binary ICO file...'));
        const blob = await createIcoBlob(frames);
        bitmap.close();
        trackEvent('tool_completed', 'image-converter');
        return {
          blob,
          filename: getConvertedFilename('ico')
        };
      }

      if (type === 'application/pdf') {
        report(45, t('جارٍ إعداد صفحة PDF عالية الدقة...', 'Preparing high-resolution PDF page...'));
        const { canvas, context } = makeCanvas(width, height, '#ffffff');
        context.drawImage(bitmap, 0, 0);
        bitmap.close();
        report(80, t('جارٍ إنشاء ملف PDF وتضمين البيانات...', 'Generating PDF and embedding image...'));
        const blob = await canvasToPDFBlob(canvas);
        trackEvent('tool_completed', 'image-converter');
        return {
          blob,
          filename: getConvertedFilename('pdf')
        };
      }

      if (type === 'image/bmp') {
        report(45, t('جارٍ تشفير مصفوفة البتات النقطية BMP...', 'Encoding BMP bitmap array...'));
        const { canvas, context } = makeCanvas(width, height, '#ffffff');
        context.drawImage(bitmap, 0, 0);
        bitmap.close();
        const blob = canvasToBMPBlob(canvas);
        trackEvent('tool_completed', 'image-converter');
        return {
          blob,
          filename: getConvertedFilename('bmp')
        };
      }

      if (type === 'image/tiff') {
        report(45, t('جارٍ إنشاء هيكل بيانات TIFF 6.0 النقطي...', 'Encoding TIFF 6.0 baseline data...'));
        const { canvas, context } = makeCanvas(width, height, null);
        context.drawImage(bitmap, 0, 0);
        bitmap.close();
        const blob = canvasToTIFFBlob(canvas);
        trackEvent('tool_completed', 'image-converter');
        return {
          blob,
          filename: getConvertedFilename('tiff')
        };
      }

      if (type === 'image/x-tga') {
        report(45, t('جارٍ تشفير ملف TGA Truevision بدقة كاملة...', 'Encoding Truevision TGA file...'));
        const { canvas, context } = makeCanvas(width, height, null);
        context.drawImage(bitmap, 0, 0);
        bitmap.close();
        const blob = canvasToTGABlob(canvas);
        trackEvent('tool_completed', 'image-converter');
        return {
          blob,
          filename: getConvertedFilename('tga')
        };
      }

      // Standard formats (JPG, PNG, WebP, AVIF, GIF)
      report(45, t('جارٍ رسم وتحويل الصورة بدقة...', 'Rendering and converting image...'));
      const bg = (type === 'image/jpeg') ? '#ffffff' : null;
      const { canvas, context } = makeCanvas(width, height, bg);
      context.drawImage(bitmap, 0, 0);
      bitmap.close();

      report(75, t('جارٍ ضغط وتشفير الملف النهائي...', 'Compressing and encoding final image...'));
      const qValue = Number(settings.quality ?? 100) / 100;
      let blob;
      try {
        blob = await canvasToBlob(canvas, type, qValue);
      } catch {
        // Fallback to PNG if browser doesn't support target format export
        blob = await canvasToBlob(canvas, 'image/png');
      }

      const ext = imageExtension(blob.type || type);
      trackEvent('tool_completed', 'image-converter');
      return {
        blob,
        filename: getConvertedFilename(ext)
      };
    }
  });
}
